import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete-tag.svg";
import Input from "../../common/InputField";
import { toast } from "react-toastify";

import { createChatTag } from "../../../store/features/createChatTag";
import { getTagsByGroupId } from "../../../store/features/getTagsByGroupId";
import { deleteBusinessTag } from "../../../store/features/deleteBusinessTag";

import { getAllKbTags } from "../../../store/features/getTagsToKnowledgeBase";
import { createKbTag, resetCreateKbTagState } from "../../../store/features/createTagToKnowledgeBase";
import { deleteKbTag } from "../../../store/features/deleteKBTag";

export const colorCodesForAvatar = [
  "#405261", "#FF4D4F", "#FA541C", "#FA8C16", "#FAAD14",
  "#EB2F96", "#FADB14", "#A0D911", "#52C41A", "#5CDBD3",
  "#9254DE", "#F759AB", "#2F54EB", "#722ED1", "#1890FF",
  "#13C2C2", "#2DB7F5", "#19BE6B", "#30A745", "#FA8C16",
  "#FAAD14", "#9254DE", "#F759AB", "#13C2C2", "#722ED1", "#2DB7F5",
];

export const chracterPosition = (alphabet) => {
  const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
  return positionOfAlphabet;
};

const TagsManagement = ({ selectedGroupId, onKbTabChange }) => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(0); // 0 = Chats & Tickets, 1 = Knowledge Base
  const [chatTags, setChatTags] = useState([]);
  const [kbTags, setKbTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [creating, setCreating] = useState(false);

  // Auth / Business info
  const loginTokenState = useSelector((state) => state.loginToken);
  const business_id = loginTokenState?.response?.user?.business_id;

  // Chat tags selectors (safe)
  const getTagsState = useSelector((state) => state.getTagsByGroupId ?? {});
  const tagsData = Array.isArray(getTagsState.data) ? getTagsState.data : [];
  const tagsLoading = getTagsState.loading ?? false;
  const tagsLastFetchedKey = getTagsState.lastFetchedKey ?? null;

  // KB tags selectors (safe)
  const kbTagsState = useSelector((state) => state.getAllKbTags ?? {});
  const kbTagsRaw = Array.isArray(kbTagsState.data) ? kbTagsState.data : kbTagsState.data?.data || [];
  const kbTagsLoading = kbTagsState.loading ?? false;
  const kbTagsHasFetchedOnce = kbTagsState.hasFetchedOnce ?? false;

  // Create / Delete loading states
  const createChatLoading = useSelector((state) => state.createChatTag?.loading ?? false);
  const createKbLoading = useSelector((state) => state.createKbTag?.loading ?? false);
  const deleteChatLoading = useSelector((state) => state.deleteBusinessTag?.loading ?? false);
  const deleteKbLoading = useSelector((state) => state.deleteKbTag?.loading ?? false);

  const currentGroupId = selectedGroupId;
  const isChatsTab = activeTab === 0;
  const currentTags = isChatsTab ? chatTags : kbTags;
  /** Avoid empty-state flash before first fetch; soft Redux loading keeps revisit instant. */
  const chatsFetchKey =
    business_id && currentGroupId ? `${business_id}::${currentGroupId}` : null;
  /** Dropdown sets `selectedGroupId` async after accessible groups load; avoid "no tags" flash before a group exists. */
  const chatsWaitingForGroup = Boolean(business_id) && !currentGroupId;
  const chatsLoadingTags =
    Boolean(chatsFetchKey) && (tagsLoading || tagsLastFetchedKey !== chatsFetchKey);
  const showTableSkeleton =
    isChatsTab
      ? chatsWaitingForGroup || chatsLoadingTags
      : Boolean(business_id) && (kbTagsLoading || !kbTagsHasFetchedOnce);
  const isCreating = creating || createChatLoading || createKbLoading;
  const deleteLoading = isChatsTab ? deleteChatLoading : deleteKbLoading;

  // Fetch chat tags (layout effect: dispatch before paint so loading is true before empty-state flash)
  useLayoutEffect(() => {
    if (business_id && currentGroupId && activeTab === 0) {
      dispatch(getTagsByGroupId({ business_id, group_id: currentGroupId }));
    }
  }, [dispatch, business_id, currentGroupId, activeTab]);

  // Format chat tags
  useEffect(() => {
    const safeTags = Array.isArray(tagsData) ? tagsData : [];
    const sorted = [...safeTags].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const formatted = sorted.map((tag) => ({
      id: tag._id,
      name: tag.name,
      author: tag.created_by?.full_name || "Unknown",
      authorAvatar: tag.created_by?.agent_image || null,
      date: tag.createdAt
        ? new Date(tag.createdAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        : "N/A",
      uses: tag.usage || { chats: 0, tickets: 0 },
    }));

    setChatTags(formatted);
  }, [tagsData]);

  // Fetch KB tags
  useLayoutEffect(() => {
    if (activeTab === 1 && business_id) {
      dispatch(getAllKbTags());
    }
  }, [activeTab, dispatch, business_id]);

  useEffect(() => {
    if (activeTab !== 1) {
      dispatch(resetCreateKbTagState());
    }
  }, [activeTab, dispatch]);

  // Format KB tags
  useEffect(() => {
    const safeKbTags = Array.isArray(kbTagsRaw) ? kbTagsRaw : [];
    const sorted = [...safeKbTags].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const formatted = sorted.map((tag) => ({
      id: tag._id,
      name: tag.name,
      author: tag.created_by?.full_name || "Unknown",
      authorAvatar: tag.created_by?.agent_image || null,
      date: tag.createdAt
        ? new Date(tag.createdAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        : "N/A",
      usageCount: tag.usageCount ?? 0,
    }));

    setKbTags(formatted);
  }, [kbTagsRaw]);

  // Notify parent component about KB tab
  useEffect(() => {
    onKbTabChange?.(activeTab === 1);
  }, [activeTab, onKbTabChange]);

  const handleCreateTag = async () => {
    const trimmedName = newTagName.trim();
    if (!trimmedName) {
      toast.error("Please enter a tag name.");
      return;
    }

    const regex = /^#?[A-Za-z0-9]+(?:[ @#-][A-Za-z0-9]+)*$/;
    if (!regex.test(trimmedName)) {
      toast.error("Tag can only include letters, numbers, spaces, @, #, or hyphens");
      return;
    }

    if (currentTags.some((t) => t.name.toLowerCase() === trimmedName.toLowerCase())) {
      toast.error("A tag with this name already exists.");
      return;
    }

    if (trimmedName.length > 32) {
      toast.error("Tag name cannot exceed 32 characters.");
      return;
    }

    setCreating(true);

    try {
      if (isChatsTab) {
        await dispatch(createChatTag({ name: trimmedName, group_id: currentGroupId })).unwrap();
        toast.success("Tag created successfully!");
        dispatch(getTagsByGroupId({ business_id, group_id: currentGroupId }));
      } else {
        await dispatch(createKbTag({ name: trimmedName })).unwrap();

        dispatch(getAllKbTags());
      }
    } catch (error) {
      toast.error(error?.message || `Failed to create ${isChatsTab ? "tag" : "Knowledge Base tag"}.`);
    }

    setNewTagName("");
    setCreating(false);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      if (isChatsTab) {
        await dispatch(deleteBusinessTag({ tagId, business_id, group_id: currentGroupId })).unwrap();
        toast.success("Tag deleted successfully!");
        dispatch(getTagsByGroupId({ business_id, group_id: currentGroupId }));
      } else {
        await dispatch(deleteKbTag(tagId)).unwrap();

        dispatch(getAllKbTags());
      }
    } catch (error) {
      toast.error(error?.message || `Failed to delete ${isChatsTab ? "tag" : "Knowledge Base tag"}.`);
    }
  };

  return (
    <Box sx={{ padding: "17px 24px 24px", backgroundColor: "#fff" }}>
      {/* Header + Create Tag */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "23px" }}>
        <Box>
          <Typography sx={{ color: "#061522", fontSize: "18px", fontWeight: 500, mb: "8px" }}>
            Tags Management
          </Typography>
          <Typography sx={{ color: "#405261", fontSize: "14px", lineHeight: "22px" }}>
            Organize customizable tags for easy categorisation
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Input
            className="tag-input"
            noLabel
            placeholder="Tag"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleCreateTag()}
            disabled={isCreating}
          />
          <Button
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
            sx={{
              width: "120px",
              height: "40px",
              borderRadius: "8px",
              background: "#148DFD",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              color: "white",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              "&:hover": { backgroundColor: "#0077CC" },
              "&.Mui-disabled": {
                bgcolor: "#F9FAFA",
                color: "#93A1B0",
                border: "1px solid #C5CED6",
              },
            }}
          >
            {isCreating ? "Creating..." : "Create Tag"}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ mb: "29px", display: "flex", gap: "20px", position: "relative" }}>
        <button
          type="button"
          onClick={() => setActiveTab(0)}
          style={{
            border: "none",
            background: "none",
            padding: "0 0 9px 0",
            fontFamily: "Inter, sans-serif",
            fontSize: 14,
            fontWeight: activeTab === 0 ? 500 : 400,
            color: activeTab === 0 ? "#061522" : "#405261",
            cursor: "pointer",
            position: "relative",
            borderBottom: activeTab === 0 ? "1px solid #148DFD" : "none",
            marginBottom: "-1px",
          }}
        >
          Chats & Tickets
        </button>
        <button
          type="button"
          onClick={() => setActiveTab(1)}
          style={{
            border: "none",
            background: "none",
            padding: "0 0 9px 0",
            fontFamily: "Inter, sans-serif",
            fontSize: 14,
            fontWeight: activeTab === 1 ? 500 : 400,
            color: activeTab === 1 ? "#061522" : "#405261",
            cursor: "pointer",
            position: "relative",
            borderBottom: activeTab === 1 ? "1px solid #148DFD" : "none",
            marginBottom: "-1px",
          }}
        >
          Knowledge Base
        </button>
      </Box>

      {/* Table */}
      <TableContainer elevation={0}>
        <Table>
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                paddingTop: "0px !important",
                paddingBottom: "9px",
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ pl: 0, color: "#405261", fontSize: 12, borderBottom: "1px solid #E5E9EC", minWidth: isChatsTab ? "auto" : "500px" }}>
                Tag
              </TableCell>

              {isChatsTab && (
                <TableCell sx={{ color: "#405261", fontSize: 12, borderBottom: "1px solid #E5E9EC" }}>
                  Applied In
                </TableCell>
              )}

              <TableCell sx={{ color: "#405261", fontSize: 12, borderBottom: "1px solid #E5E9EC" }}>
                Created by
              </TableCell>

              {!isChatsTab && (
                <TableCell sx={{ color: "#405261", fontSize: 12, borderBottom: "1px solid #E5E9EC" }}>
                  Count
                </TableCell>
              )}

              <TableCell sx={{ color: "#405261", fontSize: 12, borderBottom: "1px solid #E5E9EC" }}>
                Date
              </TableCell>

              <TableCell sx={{ width: 60, borderBottom: "1px solid #E5E9EC" }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {showTableSkeleton ? (
              Array(6)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ pl: 0 }}>
                      <Skeleton variant="rounded" width={120} height={32} />
                    </TableCell>
                    {isChatsTab && <TableCell><Skeleton width={80} height={40} /></TableCell>}
                    <TableCell><Skeleton width={140} height={32} /></TableCell>
                    {!isChatsTab && <TableCell><Skeleton width={80} /></TableCell>}
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={36} height={36} /></TableCell>
                  </TableRow>
                ))
            ) : currentTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isChatsTab ? 5 : 5} sx={{ textAlign: "center", py: 10 }}>
                  <Typography color="#405261">
                    {isChatsTab ? "No tags found for this group." : "No knowledge base tags found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              currentTags.map((tag, index) => (
                <TableRow
                  key={tag.id}
                  sx={{
                    borderBottom: index === currentTags.length - 1 ? "none" : "1px solid #E5E9EC",
                  }}
                >
                  <TableCell sx={{ pl: 0, py: 2, minWidth: isChatsTab ? "auto" : "400px" }}>
                    <Chip
                      label={tag.name}
                      sx={{
                        borderRadius: "19px",
                        background: "#F9FAFA",
                        border: "1px solid #C5CED6",
                        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
                        color: "#405261",
                        fontSize: "12px",
                        fontWeight: 500,
                        height: 32,
                      }}
                    />
                  </TableCell>

                  {isChatsTab && (
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography fontSize={14} color="#405261">
                          {tag.uses?.chats || 0} chats
                        </Typography>
                        <Typography fontSize={14} color="#405261">
                          {tag.uses?.tickets || 0} tickets
                        </Typography>
                      </Box>
                    </TableCell>
                  )}

                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={tag.authorAvatar || undefined}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: !tag.authorAvatar
                            ? colorCodesForAvatar[
                            chracterPosition((tag.author || "?").substring(0, 1).toUpperCase()) % colorCodesForAvatar.length
                            ]
                            : undefined,
                        }}
                      >
                        {!tag.authorAvatar && (tag.author || "?").substring(0, 1).toUpperCase()}
                      </Avatar>
                      <Typography fontSize={14} color="#405261">
                        {tag.author}
                      </Typography>
                    </Box>
                  </TableCell>

                  {!isChatsTab && (
                    <TableCell sx={{ py: 2, color: "#405261", fontSize: 14 }}>
                      {tag.usageCount}
                    </TableCell>
                  )}

                  <TableCell sx={{ py: 2, color: "#405261", fontSize: 14 }}>
                    {tag.date || "N/A"}
                  </TableCell>

                  <TableCell sx={{ py: 2 }}>
                    <IconButton
                      onClick={() => handleDeleteTag(tag.id)}
                      disabled={deleteLoading}
                      sx={{ color: "#ff4444", "&:hover": { bgcolor: "rgba(255,68,68,0.08)" } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TagsManagement;